import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GameWrapper from '@/pages/jeux/components/GameWrapper';
import GameOverModal from '@/pages/jeux/components/GameOverModal';
import { useGame } from '@/contexts/GameContext';
import { HEALTH_TIPS } from '@/mocks/jeux-data';

type Player = 'medecin' | 'virus';
type Cell = Player | null;
type Board = Cell[];

const WIN_LINES = [
  [0, 1, 2], [3, 4, 5], [6, 7, 8],
  [0, 3, 6], [1, 4, 7], [2, 5, 8],
  [0, 4, 8], [2, 4, 6],
];

function checkWinner(board: Board): Player | null {
  for (const [a, b, c] of WIN_LINES) {
    if (board[a] && board[a] === board[b] && board[a] === board[c]) return board[a];
  }
  return null;
}

function getWinningLine(board: Board): number[] | null {
  for (const line of WIN_LINES) {
    const [a, b, c] = line;
    if (board[a] && board[a] === board[b] && board[a] === board[c]) return line;
  }
  return null;
}

function aiMove(board: Board): number {
  const empty = board.reduce<number[]>((acc, c, i) => {
    if (c === null) acc.push(i);
    return acc;
  }, []);

  if (empty.length === 0) return -1;

  for (const i of empty) {
    const test = [...board];
    test[i] = 'virus';
    if (checkWinner(test) === 'virus') return i;
  }
  for (const i of empty) {
    const test = [...board];
    test[i] = 'medecin';
    if (checkWinner(test) === 'medecin') return i;
  }
  if (empty.includes(4)) return 4;

  const corners = [0, 2, 6, 8].filter((c) => empty.includes(c));
  if (corners.length > 0) return corners[Math.floor(Math.random() * corners.length)];

  return empty[Math.floor(Math.random() * empty.length)];
}

export default function TicTacToe() {
  const navigate = useNavigate();
  const { startGame, endGame, gameScore, gameTime, gameErrors, updateScore, updateErrors } = useGame();
  const [board, setBoard] = useState<Board>(Array(9).fill(null));
  const [currentPlayer, setCurrentPlayer] = useState<Player>('medecin');
  const [roundResult, setRoundResult] = useState<'medecin' | 'virus' | 'draw' | null>(null);
  const [winningLine, setWinningLine] = useState<number[] | null>(null);
  const [showGameOver, setShowGameOver] = useState(false);
  const [roundCount, setRoundCount] = useState(0);
  const [playerWins, setPlayerWins] = useState(0);
  const [virusWins, setVirusWins] = useState(0);
  const [draws, setDraws] = useState(0);
  const [aiThinking, setAiThinking] = useState(false);
  const [totalScore, setTotalScore] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const aiTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boardRef = useRef<Board>(board);

  useEffect(() => {
    boardRef.current = board;
  }, [board]);

  useEffect(() => {
    return () => {
      if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current);
    };
  }, []);

  const handleStart = useCallback(() => {
    setBoard(Array(9).fill(null));
    setCurrentPlayer('medecin');
    setRoundResult(null);
    setWinningLine(null);
    setShowGameOver(false);
    setAiThinking(false);
    setTotalScore(0);
    setGameStarted(true);
    startGame('tic-tac-toe');
  }, [startGame]);

  const handleRoundEnd = useCallback(
    (result: 'medecin' | 'virus' | 'draw', line: number[] | null, currentRoundScore: number) => {
      setRoundResult(result);
      setWinningLine(line);
      setAiThinking(false);
      const newTotal = totalScore + currentRoundScore;
      setTotalScore(newTotal);
      updateScore(newTotal);

      if (result === 'medecin') {
        setPlayerWins((p) => p + 1);
      } else if (result === 'virus') {
        setVirusWins((p) => p + 1);
        updateErrors(gameErrors + 1);
      } else {
        setDraws((p) => p + 1);
      }
    },
    [totalScore, gameErrors, updateScore, updateErrors],
  );

  const makeAiMove = useCallback(
    (currentBoard: Board) => {
      setAiThinking(true);
      aiTimeoutRef.current = setTimeout(() => {
        const b = boardRef.current;
        const move = aiMove(b);
        if (move < 0) {
          setAiThinking(false);
          return;
        }
        const newBoard = [...b];
        newBoard[move] = 'virus';
        setBoard(newBoard);

        const w = checkWinner(newBoard);
        if (w === 'virus') {
          const line = getWinningLine(newBoard);
          handleRoundEnd('virus', line, 0);
        } else if (newBoard.every((c) => c !== null)) {
          handleRoundEnd('draw', null, 25);
        } else {
          setAiThinking(false);
          setCurrentPlayer('medecin');
        }
      }, 500);
    },
    [handleRoundEnd],
  );

  const handleClick = useCallback(
    (idx: number) => {
      const b = boardRef.current;
      if (b[idx] || roundResult || currentPlayer !== 'medecin' || aiThinking) return;

      const newBoard = [...b];
      newBoard[idx] = 'medecin';
      setBoard(newBoard);

      const w = checkWinner(newBoard);
      if (w === 'medecin') {
        const line = getWinningLine(newBoard);
        handleRoundEnd('medecin', line, 150);
      } else if (newBoard.every((c) => c !== null)) {
        handleRoundEnd('draw', null, 25);
      } else {
        setCurrentPlayer('virus');
        makeAiMove(newBoard);
      }
    },
    [currentPlayer, roundResult, aiThinking, makeAiMove, handleRoundEnd],
  );

  const handleNextRound = () => {
    if (roundCount >= 9) {
      endGame(totalScore, gameTime, gameErrors);
      setShowGameOver(true);
      return;
    }
    setBoard(Array(9).fill(null));
    setCurrentPlayer('medecin');
    setRoundResult(null);
    setWinningLine(null);
    setAiThinking(false);
    setRoundCount((r) => r + 1);
  };

  const handleQuitRounds = () => {
    endGame(totalScore, gameTime, gameErrors);
    setShowGameOver(true);
  };

  const handleReplay = () => {
    setRoundCount(0);
    setPlayerWins(0);
    setVirusWins(0);
    setDraws(0);
    setGameStarted(true);
    handleStart();
  };

  const handleQuit = () => {
    if (aiTimeoutRef.current) clearTimeout(aiTimeoutRef.current);
    setGameStarted(false);
    endGame(gameScore, gameTime, gameErrors);
    navigate('/jeux', { replace: true });
  };

  if (!gameStarted) {
    return (
      <GameWrapper gameName="Tic-Tac-Toe Santé" onQuit={handleQuit} showPause={false}>
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="bg-background-50 rounded-3xl p-8 max-w-md w-full text-center border border-background-200/70">
            <div className="flex items-center justify-center gap-4 mb-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                <i className="ri-stethoscope-line text-2xl text-green-600"></i>
              </div>
              <span className="text-2xl font-bold text-foreground-400">VS</span>
              <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                <i className="ri-virus-line text-2xl text-red-600"></i>
              </div>
            </div>
            <h2 className="text-xl font-bold text-foreground-950 mb-2">Medecin contre Virus</h2>
            <p className="text-foreground-600 text-sm mb-6">Joue le medecin et elimine tous les virus !<br />10 manches, le meilleur score gagne.</p>
            <button
              onClick={handleStart}
              className="w-full py-3 bg-primary-500 text-background-50 rounded-xl font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill mr-2"></i> Commencer la partie
            </button>
          </div>
        </div>
      </GameWrapper>
    );
  }

  const statusText = roundResult
    ? roundResult === 'medecin'
      ? 'Tu as gagne cette manche !'
      : roundResult === 'virus'
        ? 'Le virus a gagne cette manche...'
        : 'Match nul !'
    : aiThinking
      ? 'Le virus reflechit...'
      : currentPlayer === 'medecin'
        ? 'A toi de jouer (Medecin)'
        : '';

  return (
    <GameWrapper gameName="Tic-Tac-Toe Santé" onQuit={handleQuitRounds}>
      <div className="flex-1 flex flex-col items-center justify-center p-4">
        {/* Scoreboard */}
        <div className="flex items-center gap-6 mb-2">
          <div className="text-center">
            <div className="flex items-center gap-1.5">
              <i className="ri-stethoscope-line text-green-600"></i>
              <span className="text-sm font-bold text-green-700">{playerWins}</span>
            </div>
            <span className="text-xs text-foreground-500">Medecin</span>
          </div>
          <div className="text-center">
            <span className="text-sm font-bold text-foreground-400">{draws}</span>
            <div className="text-xs text-foreground-500">Nul</div>
          </div>
          <div className="text-center">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-red-700">{virusWins}</span>
              <i className="ri-virus-line text-red-600"></i>
            </div>
            <span className="text-xs text-foreground-500">Virus</span>
          </div>
        </div>

        {/* Round info */}
        <p className="text-xs text-foreground-500 mb-3">
          Manche {roundCount + 1}/10
        </p>

        {/* Status */}
        <p className={`text-sm font-medium mb-4 px-4 py-1.5 rounded-full ${
          roundResult === 'medecin'
            ? 'bg-green-100 text-green-700'
            : roundResult === 'virus'
              ? 'bg-red-100 text-red-700'
              : roundResult === 'draw'
                ? 'bg-background-100 text-foreground-600'
                : aiThinking
                  ? 'bg-amber-100 text-amber-700 animate-pulse'
                  : 'bg-background-100 text-foreground-700'
        }`}>
          {aiThinking && <i className="ri-brain-line mr-1.5"></i>}
          {statusText}
        </p>

        {/* Grille */}
        <div className="grid grid-cols-3 gap-2 w-full max-w-xs">
          {board.map((cell, idx) => {
            const isWinCell = winningLine?.includes(idx);
            return (
              <button
                key={idx}
                onClick={() => handleClick(idx)}
                disabled={!!cell || !!roundResult || aiThinking}
                className={`aspect-square rounded-xl border-2 flex items-center justify-center transition-all ${
                  !cell && !roundResult && !aiThinking
                    ? 'bg-background-100 border-background-200 hover:border-primary-500/50 cursor-pointer'
                    : cell === 'medecin'
                      ? isWinCell
                        ? 'bg-green-200 border-green-500 scale-105'
                        : 'bg-green-100 border-green-400'
                      : cell === 'virus'
                        ? isWinCell
                          ? 'bg-red-200 border-red-500 scale-105'
                          : 'bg-red-100 border-red-400'
                        : 'bg-background-100 border-background-200'
                }`}
              >
                {cell === 'medecin' && (
                  <i className={`ri-stethoscope-line ${isWinCell ? 'text-green-700 text-4xl' : 'text-green-600 text-3xl'}`}></i>
                )}
                {cell === 'virus' && (
                  <i className={`ri-virus-line ${isWinCell ? 'text-red-700 text-4xl' : 'text-red-600 text-3xl'}`}></i>
                )}
              </button>
            );
          })}
        </div>

        {/* Post-round actions */}
        {roundResult && (
          <div className="mt-6 flex flex-col items-center gap-3">
            <div className="flex gap-3">
              <button
                onClick={handleNextRound}
                className="py-2.5 px-5 bg-primary-500 text-background-50 rounded-xl font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-skip-forward-line mr-1.5"></i> Manche suivante
              </button>
              <button
                onClick={handleQuitRounds}
                className="py-2.5 px-5 bg-background-100 text-foreground-700 rounded-xl font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-stop-line mr-1.5"></i> Arreter
              </button>
            </div>
          </div>
        )}
      </div>

      {showGameOver && (
        <GameOverModal
          score={totalScore}
          timeElapsed={gameTime}
          errors={gameErrors}
          gameName="tic-tac-toe"
          message={
            playerWins > virusWins
              ? 'Victoire du Medecin !'
              : virusWins > playerWins
                ? 'Le Virus l\'emporte...'
                : 'Egalite parfaite !'
          }
          onReplay={handleReplay}
          onQuit={handleQuit}
          healthTip={HEALTH_TIPS[Math.floor(Math.random() * HEALTH_TIPS.length)]}
        />
      )}
    </GameWrapper>
  );
}